import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import { DLODropdownOption } from '../../types/dlo.types';
import { DayItem } from '../DLOFilterByDayModal';
import DLOTooltip from '../DLOTooltip';

export interface ActualHoursEmployeeRowData {
  id: string;
  isSelected: boolean;
  employee: DLODropdownOption | null;
  role: DLODropdownOption | null;
  shift: DLODropdownOption | null;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  total: string;
  scheduleVariance: string;
  // Split role fields
  isSplit?: boolean;
  parentId?: string;
  childId?: string;
  isChildRow?: boolean;
  isLocked?: boolean;
  overrideActualHours?: { [key: string]: number | null } | number | null;
  sourceSnapshot?: {
    employee: DLODropdownOption | null;
    role: DLODropdownOption | null;
    shift: DLODropdownOption | null;
    sunday: string;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
  };
}

export interface DLOCreateActualHoursEmployeeRowProps {
  data: ActualHoursEmployeeRowData;
  onUpdate: (id: string, updatedData: Partial<ActualHoursEmployeeRowData>) => void;
  onDelete?: (id: string) => void;
  employeeOptions: DLODropdownOption[];
  roleOptions: DLODropdownOption[];
  shiftOptions: DLODropdownOption[];
  selectedDays: string[];
  dayFilter?: DayItem[];
  // All employee rows for calculating totals including child split rows
  allEmployeeRows?: ActualHoursEmployeeRowData[];
  // Schedule employee rows for calculating schedule variance
  scheduleEmployeeRows?: any[];
  // Function to update multiple rows (for parent checkbox selecting all children)
  onUpdateMultiple?: (updates: Array<{ id: string; updatedData: Partial<ActualHoursEmployeeRowData> }>) => void;
  // Global dropdown management props
  isEmployeeDropdownOpen?: boolean;
  isRoleDropdownOpen?: boolean;
  isShiftDropdownOpen?: boolean;
  onDropdownOpen?: (rowId: string, type: 'employee' | 'role' | 'shift', isOpen: boolean) => void;
  // Loading state props
  isEmployeeLoading?: boolean;
  isRoleLoading?: boolean;
  isShiftLoading?: boolean;
  // Input field control for week cutoff
  inputsEnabled?: boolean;
  disabledDays?: string[]; // <-- Add this prop
}

const DLOCreateActualHoursEmployeeRow: React.FC<DLOCreateActualHoursEmployeeRowProps> = ({
  data,
  onUpdate,
  onDelete,
  employeeOptions,
  roleOptions,
  shiftOptions,
  selectedDays,
  dayFilter = [],
  // All employee rows for calculating totals including child split rows
  allEmployeeRows = [],
  // Schedule employee rows for calculating schedule variance
  scheduleEmployeeRows = [],
  // Function to update multiple rows (for parent checkbox selecting all children)
  onUpdateMultiple,
  // Global dropdown management props
  isEmployeeDropdownOpen = false,
  isRoleDropdownOpen = false,
  isShiftDropdownOpen = false,
  onDropdownOpen,
  // Loading state props
  isEmployeeLoading = false,
  isRoleLoading = false,
  isShiftLoading = false,
  // Input field control for week cutoff
  inputsEnabled = true,
  disabledDays = [],
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Use global dropdown state instead of local state
  const employeeDropdownOpen = isEmployeeDropdownOpen;
  const roleDropdownOpen = isRoleDropdownOpen;
  const shiftDropdownOpen = isShiftDropdownOpen;

  // Check if a day is disabled based on day filter or disabledDays
  const isDayDisabled = (dayName: string): boolean => {
    if (disabledDays && disabledDays.includes(dayName.toLowerCase())) return true;
    const dayItem = dayFilter.find(item => item.name.toLowerCase() === dayName.toLowerCase());
    return dayItem ? !dayItem.enabled : false;
  };
  const shouldShowShiftError = (): boolean => {
    // Show shift error if employee and role are selected but shift is not selected
    return !!(data.employee && data.role && !data.shift);
  };

   const shouldShowEmployeeError = (): boolean => {
    // Show employee error if shift has value and employee is null
    // OR if employee exists but its ID doesn't match any available employee options
    return (data.employee === null && data.shift !== null) ||
           (data.employee !== null && !employeeOptions.some(emp => emp.id === data.employee?.id));
  };
  // Transform DLODropdownOption to DropDownPicker format
  const transformOptions = (options: DLODropdownOption[]) => 
    options.map(option => ({
      label: option.gpid ? `${option.label} ${option.gpid}` : option.label,
      value: option.id,
      key: option.id
    }));

  // Memoize transformed options so DropDownPicker Picker gets a stable array
  // reference and doesn't trigger its internal useEffect → setState infinite loop.
  const transformedEmployeeOptions = useMemo(
    () => transformOptions(employeeOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeOptions]
  );
  const transformedRoleOptions = useMemo(
    () => transformOptions(roleOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleOptions]
  );
  const transformedShiftOptions = useMemo(
    () => transformOptions(shiftOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftOptions]
  );

  // Render loading content for dropdowns
  const renderLoadingContent = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="small" color="#005CBC" />
      <Text style={styles.loadingText}>Loading...</Text>
    </View>
  );

  // Functions to handle opening dropdowns using global state management
  const handleEmployeeDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(employeeDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'employee', newValue);
  };

  const handleRoleDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(roleDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'role', newValue);
  };

  const handleShiftDropdownOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(shiftDropdownOpen) : value;
    onDropdownOpen?.(data.id, 'shift', newValue);
  };

  // Validation logic for sequential dropdown enabling
  const isEmployeeDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee || data.role || data.shift) {
      return inputsEnabled;
    }
    // For new rows, employee dropdown is always enabled first
    return inputsEnabled;
  };

  const isRoleDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee && data.role) {
      return inputsEnabled;
    }
    // For new rows, only enable if employee is selected
    return inputsEnabled && !!data.employee;
  };

  const isShiftDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing employee row)
    if (data.employee && data.role && data.shift) {
      return inputsEnabled;
    }
    // For new rows, only enable if both employee and role are selected
    return inputsEnabled && !!data.employee && !!data.role;
  };

  const handleEmployeeSelect = (value: string | null) => {
    const selectedEmployee = value ? employeeOptions.find(emp => emp.id === value) : null;
    // When employee changes, clear role and shift if this is a new row
    const updates: Partial<ActualHoursEmployeeRowData> = { employee: selectedEmployee || null };
    
    // If this was a new row without prepopulated data, clear dependent fields
    if (!data.employee && !data.role && !data.shift) {
      updates.role = null;
      updates.shift = null;
    }
    
    onUpdate(data.id, updates);
  };

  const handleRoleSelect = (value: string | null) => {
    const selectedRole = value ? roleOptions.find(role => role.id === value) : null;
    // When role changes, clear shift if this is a new row
    const updates: Partial<ActualHoursEmployeeRowData> = { role: selectedRole || null };
    
    // If this was a new row without prepopulated data, clear dependent fields
    if (!data.shift || (!data.employee && !data.role && !data.shift)) {
      updates.shift = null;
    }
    
    onUpdate(data.id, updates);
  };

  const handleShiftSelect = (value: string | null) => {
    const selectedShift = value ? shiftOptions.find(shift => shift.id === value) : null;
    onUpdate(data.id, { shift: selectedShift || null });
  };

  // Helper function to get the effective actual hours value for a day
  // Gives preference to overrideActualHours, falls back to regular day value
  const getEffectiveDayValue = (day: string): string => {
    if (data.overrideActualHours && typeof data.overrideActualHours === 'object') {
      const overrideValue = data.overrideActualHours[day];
      if (overrideValue !== null && overrideValue !== undefined) {
        return overrideValue.toString();
      }
    }
    return (data[day as keyof ActualHoursEmployeeRowData] as string) || '';
  };

  // Helper function to get effective day value for any employee row
  const getEffectiveDayValueForRow = (row: ActualHoursEmployeeRowData, day: string): string => {
    if (row.overrideActualHours && typeof row.overrideActualHours === 'object') {
      const overrideValue = row.overrideActualHours[day];
      if (overrideValue !== null && overrideValue !== undefined) {
        return overrideValue.toString();
      }
    }
    return (row[day as keyof ActualHoursEmployeeRowData] as string) || '';
  };

  const handleHourChange = (day: string, value: string) => {
    // Allow only numeric input
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    const updates: Partial<ActualHoursEmployeeRowData> = { [day]: numericValue };
    
    // Recalculate total
    const weekdayValues = [
      day === 'sunday' ? numericValue : data.sunday,
      day === 'monday' ? numericValue : data.monday,
      day === 'tuesday' ? numericValue : data.tuesday,
      day === 'wednesday' ? numericValue : data.wednesday,
      day === 'thursday' ? numericValue : data.thursday,
      day === 'friday' ? numericValue : data.friday,
      day === 'saturday' ? numericValue : data.saturday
    ];

    const total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);
    
    updates.total = total.toString();
    onUpdate(data.id, updates);
  };

  const handleCheckboxPress = () => {
    if (!data.isChildRow && data.isSplit && onUpdateMultiple) {
      // Parent row: if selected, deselect parent and all children; if not selected, select parent and all children
      const childRows = allEmployeeRows.filter((row: ActualHoursEmployeeRowData) => 
        row.parentId === data.id && row.isChildRow
      );
      
      const newSelectionState = !data.isSelected;
      const updates = [
        { id: data.id, updatedData: { isSelected: newSelectionState } }
      ];
      
      // Add child row updates
      childRows.forEach((childRow: ActualHoursEmployeeRowData) => {
        updates.push({ id: childRow.id, updatedData: { isSelected: newSelectionState } });
      });
      
      onUpdateMultiple(updates);
    } else {
      // Regular row or child row: just toggle selection
      onUpdate(data.id, { isSelected: !data.isSelected });
    }
  };

  const calculateTotalHours = (): string => {
    // Only sum enabled days from dayFilter (passed as prop)
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday'
    ];

    // If no dayFilter provided, fallback to all days enabled
    const enabledDays = dayFilter && dayFilter.length > 0
      ? dayFilter.filter((day: any) => day.enabled).map((day: any) => day.name.toLowerCase())
      : dayNames;

    if (!data.isChildRow && data.isSplit && allEmployeeRows.length > 0) {
      // Parent row with children: include parent's own hours + all child hours, only for enabled days
      const parentTotal = dayNames.reduce((sum, day) => {
        if (enabledDays.includes(day)) {
          const value = parseFloat(getEffectiveDayValue(day) || '0') || 0;
          return sum + value;
        }
        return sum;
      }, 0);

      // Find child rows for this parent
      const childRows = allEmployeeRows.filter((row: ActualHoursEmployeeRowData) => 
        row.parentId === data.id && row.isChildRow
      );

      // Calculate total of all child hours for enabled days
      const childrenTotal = childRows.reduce((sum, childRow) => {
        const childTotal = dayNames.reduce((childSum, day) => {
          if (enabledDays.includes(day)) {
            const value = parseFloat(getEffectiveDayValueForRow(childRow, day) || '0') || 0;
            return childSum + value;
          }
          return childSum;
        }, 0);
        return sum + childTotal;
      }, 0);

      return (parentTotal + childrenTotal).toFixed(2);
    } else {
      // Child row or parent without children: only own hours for enabled days
      const total = dayNames.reduce((sum, day) => {
        if (enabledDays.includes(day)) {
          const value = parseFloat(getEffectiveDayValue(day) || '0') || 0;
          return sum + value;
        }
        return sum;
      }, 0);
      return total.toFixed(2);
    }
  };

  // Hours validation functions for yellow highlighting
  const getShiftData = (shift: DLODropdownOption | null): { id: string; shift_type: string; time_format: string; time_value: string } | null => {
    if (!shift || !shift.value) return null;
    
    try {
      const shiftData = JSON.parse(shift.value);
      return shiftData;
    } catch (error) {
      return {
        id: shift.id,
        shift_type: '',
        time_format: '',
        time_value: ''
      };
    }
  };
  
  const getShiftMaxHours = (): number => {
    const shiftData = getShiftData(data.shift);
    if (shiftData && shiftData.shift_type) {
      const shiftType = shiftData.shift_type.toLowerCase();
      if (shiftType.includes('5x8')) return 8;
      if (shiftType.includes('4x10')) return 10;
    }
    
    // Fallback to checking label if shift_type is not available
    const shiftLabel = data.shift?.label?.toLowerCase() || '';
    if (shiftLabel.includes('5x8')) return 8;
    if (shiftLabel.includes('4x10')) return 10;
    
    return 12; // Default max hours per day
  };

  const getDayHours = (day: string): number => {
    const dayValues: { [key: string]: string } = {
      sunday: data.sunday,
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday,
      saturday: data.saturday
    };
    return parseFloat(dayValues[day] || '0') || 0;
  };

  const getWeeklyHours = (): number => {
    const weekdayValues = [
      data.sunday,
      data.monday,
      data.tuesday,
      data.wednesday,
      data.thursday,
      data.friday,
      data.saturday
    ];

    let total = weekdayValues.reduce((sum, value) => {
      const numericValue = parseFloat(value) || 0;
      return sum + numericValue;
    }, 0);

    // If this is a parent row (not a child row and has split roles), 
    // add the hours from all child split rows
    if (!data.isChildRow && data.isSplit && allEmployeeRows.length > 0) {
      const childRows = allEmployeeRows.filter(row => row.parentId === data.id);
      
      childRows.forEach(childRow => {
        const childWeekdayValues = [
          childRow.sunday,
          childRow.monday,
          childRow.tuesday,
          childRow.wednesday,
          childRow.thursday,
          childRow.friday,
          childRow.saturday
        ];
        
        const childTotal = childWeekdayValues.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);
        
        total += childTotal;
      });
    }

    return total;
  };

  const isDayHoursExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    return dayHours > maxHours;
  };

  const isDailyMaxExceeded = (day: string): boolean => {
    const dayHours = getDayHours(day);
    return dayHours > 12; // General daily max is 12 hours
  };

  const isWeeklyMaxExceeded = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 60; // Weekly max is 60 hours
  };

  const isWeeklyHighlightThreshold = (): boolean => {
    const weeklyHours = getWeeklyHours();
    return weeklyHours > 40; // Highlight after 40 hours
  };

  const getValidationMessage = (day: string): string => {
    if (day === 'total') {
      if (isWeeklyMaxExceeded()) {
        return 'Weekly hours exceeded maximum of 60 hours';
      }
      if (isWeeklyHighlightThreshold()) {
        return 'Weekly hours exceed 40 hours';
      }
      return '';
    }

    const dayHours = getDayHours(day);
    const maxHours = getShiftMaxHours();
    
    if (isDailyMaxExceeded(day)) {
      return 'Daily hours exceeded maximum of 12 hours';
    }
    
    if (isDayHoursExceeded(day)) {
      const shiftLabel = data.shift?.label || 'shift';
      return `Daily hours exceeded ${shiftLabel} maximum of ${maxHours} hours`;
    }
    
    return '';
  };

  const shouldHighlightCell = (day: string): boolean => {
    if (day === 'total') {
      return isWeeklyMaxExceeded() || isWeeklyHighlightThreshold();
    }
    return isDailyMaxExceeded(day) || isDayHoursExceeded(day);
  };

  const calculateScheduleVariance = (): string => {
    // Schedule Variance = (Total Scheduled Hours - Total Actual Hours)
    
    try {
      // Check if schedule data is available
      if (!scheduleEmployeeRows || scheduleEmployeeRows.length === 0) {
        return "0.00";
      }
      
      // Find corresponding schedule row
      // Actual row IDs have format "actual_XXX", need to find schedule row with "schedule_XXX" or just "XXX"
      let correspondingScheduleRow: any = null;
      
      if (data.id.startsWith('actual_')) {
        const scheduleId = data.id.replace('actual_', 'schedule_');
        correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === scheduleId);
        
        // If not found with "schedule_" prefix, try without any prefix
        if (!correspondingScheduleRow) {
          const baseId = data.id.replace('actual_', '');
          correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === baseId);
        }
      } else {
        // If actual ID doesn't have "actual_" prefix, try direct match or with "schedule_" prefix
        correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === data.id);
        if (!correspondingScheduleRow) {
          correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === `schedule_${data.id}`);
        }
      }
      
      if (!correspondingScheduleRow) {
        return "0.00";
      }
      
      // Calculate total scheduled hours for this row
      let totalScheduledHours = 0;
      
      // For parent rows with children, include both parent and child hours
      if (!correspondingScheduleRow.isChildRow && correspondingScheduleRow.isSplit && scheduleEmployeeRows.length > 0) {
        // Parent row: include parent's own hours + all child hours
        const parentScheduleHours = [
          correspondingScheduleRow.sunday,
          correspondingScheduleRow.monday,
          correspondingScheduleRow.tuesday,
          correspondingScheduleRow.wednesday,
          correspondingScheduleRow.thursday,
          correspondingScheduleRow.friday,
          correspondingScheduleRow.saturday
        ];
        
        const parentTotal = parentScheduleHours.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);
        
        // Find child rows for this parent in schedule
        const childScheduleRows = scheduleEmployeeRows.filter(row => 
          row.parentId === correspondingScheduleRow.id && row.isChildRow
        );
        
        // Calculate total of all child scheduled hours
        const childrenTotal = childScheduleRows.reduce((sum, childRow) => {
          const childScheduleHours = [
            childRow.sunday,
            childRow.monday,
            childRow.tuesday,
            childRow.wednesday,
            childRow.thursday,
            childRow.friday,
            childRow.saturday
          ];
          
          const childTotal = childScheduleHours.reduce((childSum, value) => {
            const numericValue = parseFloat(value) || 0;
            return childSum + numericValue;
          }, 0);
          
          return sum + childTotal;
        }, 0);
        
        totalScheduledHours = parentTotal + childrenTotal;
      } else {
        // Child row or individual row: only own scheduled hours
        const scheduleHours = [
          correspondingScheduleRow.sunday,
          correspondingScheduleRow.monday,
          correspondingScheduleRow.tuesday,
          correspondingScheduleRow.wednesday,
          correspondingScheduleRow.thursday,
          correspondingScheduleRow.friday,
          correspondingScheduleRow.saturday
        ];
        
        totalScheduledHours = scheduleHours.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);
      }
      
      // Calculate total actual hours (use the same logic as calculateTotalHours)
      let totalActualHours = 0;
      
      if (!data.isChildRow && data.isSplit && allEmployeeRows && allEmployeeRows.length > 0) {
        // Parent row with children: include parent's own hours + all child hours
        const parentActualHours = [
          getEffectiveDayValue('sunday'),
          getEffectiveDayValue('monday'),
          getEffectiveDayValue('tuesday'),
          getEffectiveDayValue('wednesday'),
          getEffectiveDayValue('thursday'),
          getEffectiveDayValue('friday'),
          getEffectiveDayValue('saturday')
        ];

        const parentTotal = parentActualHours.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);

        // Find child rows for this parent in actual
        const childActualRows = allEmployeeRows.filter((row: ActualHoursEmployeeRowData) => 
          row.parentId === data.id && row.isChildRow
        );

        // Calculate total of all child actual hours
        const childrenTotal = childActualRows.reduce((sum, childRow) => {
          const childActualHours = [
            getEffectiveDayValueForRow(childRow, 'sunday'),
            getEffectiveDayValueForRow(childRow, 'monday'),
            getEffectiveDayValueForRow(childRow, 'tuesday'),
            getEffectiveDayValueForRow(childRow, 'wednesday'),
            getEffectiveDayValueForRow(childRow, 'thursday'),
            getEffectiveDayValueForRow(childRow, 'friday'),
            getEffectiveDayValueForRow(childRow, 'saturday')
          ];
          
          const childTotal = childActualHours.reduce((childSum, value) => {
            const numericValue = parseFloat(value) || 0;
            return childSum + numericValue;
          }, 0);
          
          return sum + childTotal;
        }, 0);

        totalActualHours = parentTotal + childrenTotal;
      } else {
        // Child row or individual row: only own actual hours
        const actualHours = [
          getEffectiveDayValue('sunday'),
          getEffectiveDayValue('monday'),
          getEffectiveDayValue('tuesday'),
          getEffectiveDayValue('wednesday'),
          getEffectiveDayValue('thursday'),
          getEffectiveDayValue('friday'),
          getEffectiveDayValue('saturday')
        ];

        totalActualHours = actualHours.reduce((sum, value) => {
          const numericValue = parseFloat(value) || 0;
          return sum + numericValue;
        }, 0);
      }
      
      // Calculate variance: (Total Scheduled Hours - Total Actual Hours)
      // Always show as positive value using Math.abs()
      const variance = Math.abs(totalScheduledHours - totalActualHours);
      
      return variance.toFixed(2);
      
    } catch (error) {
      console.error('Error calculating schedule variance:', error);
      return "0.00";
    }
  };

  const getDayAbbreviation = (day: string): string => {
    switch (day.toLowerCase()) {
      case 'sunday': return 'sun';
      case 'monday': return 'mon';
      case 'tuesday': return 'tue';
      case 'wednesday': return 'wed';
      case 'thursday': return 'thu';
      case 'friday': return 'fri';
      case 'saturday': return 'sat';
      default: return day;
    }
  };

  const isHighlighted = (day: string): boolean => {
    if (!data.isSelected || day === 'total' || day === 'scheduleVariance') return false;
    const dayAbbr = getDayAbbreviation(day);
    return selectedDays.includes(dayAbbr);
  };

  const hasSelectedDays = (): boolean => {
    return data.isSelected && selectedDays.length > 0;
  };

  return (
    <View style={[
      styles.container, 
      data.isSelected && styles.containerSelected,
      hasSelectedDays() && styles.containerHighlighted
    ]}>
      {/* Checkbox */}
      <View style={[
        styles.checkboxContainer,
        data.isSelected && styles.checkboxContainerSelected,
        hasSelectedDays() && styles.checkboxContainerHighlighted
      ]}>
        <TouchableOpacity 
          onPress={handleCheckboxPress}
          activeOpacity={0.7}
        >
          <View style={[
            styles.checkbox,
            data.isSelected && styles.checkboxSelected
          ]}>
            {data.isSelected && (
              <Text style={styles.checkboxCheck}>✓</Text>
            )}
          </View>
        </TouchableOpacity>
      </View>
      <View style={styles.divider} />

      {/* Employee Dropdown */}
      <View style={[
        styles.dropdownSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isEmployeeLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={employeeDropdownOpen}
            value={data.employee?.id || null}
            items={transformedEmployeeOptions}
            setOpen={handleEmployeeDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.employee?.id || null) : callback;
              handleEmployeeSelect(newValue);
            }}
            onSelectItem={(item) => {
              handleEmployeeSelect(item.value || null);
            }}
            placeholder="Select Employee"
            disabled={!isEmployeeDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isEmployeeDropdownEnabled() && styles.dropdownStyleDisabled,
              shouldShowEmployeeError() && styles.dropdownStyleError
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={3000}
            zIndexInverse={1000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>
      <View style={styles.divider} />

      {/* Role Dropdown */}
      <View style={[
        styles.roleSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isRoleLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={roleDropdownOpen}
            value={data.role?.id || null}
            items={transformedRoleOptions}
            setOpen={handleRoleDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.role?.id || null) : callback;
              handleRoleSelect(newValue);
            }}
            onSelectItem={(item) => {
              handleRoleSelect(item.value || null);
            }}
            placeholder="Select Role"
            disabled={!isRoleDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isRoleDropdownEnabled() && styles.dropdownStyleDisabled
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={2000}
            zIndexInverse={2000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>
      <View style={styles.divider} />

      {/* Shift Dropdown */}
      <View style={[
        styles.shiftSection,
        data.isSelected && styles.dropdownSectionSelected,
        hasSelectedDays() && styles.dropdownSectionHighlighted
      ]}>
        {isShiftLoading ? (
          renderLoadingContent()
        ) : (
          <DropDownPicker
            open={shiftDropdownOpen}
            value={data.shift?.id || null}
            items={transformedShiftOptions}
            setOpen={handleShiftDropdownOpen}
            setValue={(callback) => {
              const newValue = typeof callback === 'function' ? callback(data.shift?.id || null) : callback;
              handleShiftSelect(newValue);
            }}
            onSelectItem={(item) => {
              handleShiftSelect(item.value || null);
            }}
            placeholder="Select Shift"
            disabled={!isShiftDropdownEnabled()}
            style={[
              styles.dropdownStyle,
              data.isSelected && styles.dropdownStyleSelected,
              hasSelectedDays() && styles.dropdownStyleHighlighted,
              !isShiftDropdownEnabled() && styles.dropdownStyleDisabled,
              shouldShowShiftError() && styles.dropdownStyleError
            ]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            listItemLabelStyle={styles.dropdownListItemLabel}
            listItemContainerStyle={styles.dropdownListItemContainer}
            searchContainerStyle={styles.dropdownSearchContainer}
            searchTextInputStyle={styles.dropdownSearchInput}
            searchPlaceholder=""
            maxHeight={200}
            zIndex={1000}
            zIndexInverse={3000}
            searchable={true}
            closeAfterSelecting={true}
            dropDownDirection='TOP'
          />
        )}
      </View>

      <View style={styles.divider} />

      {/* Day Input Fields */}
      {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => {
        const hasValidationError = shouldHighlightCell(day);
        const validationMessage = getValidationMessage(day);
        const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id); // Find matching schedule row
        const scheduleHours = scheduleRow ? scheduleRow[day as keyof ActualHoursEmployeeRowData] : undefined;
        
        // Check if this day has override actual hours
        const hasOverrideActualHours = data.overrideActualHours && 
          typeof data.overrideActualHours === 'object' && 
          (data.overrideActualHours as { [key: string]: number | null })[day] !== null && 
          (data.overrideActualHours as { [key: string]: number | null })[day] !== undefined;
        
        // Generate tooltip content based on whether cell has override or regular actual hours
        const getTooltipContent = () => {
          if (scheduleHours === undefined) return '';
          
          if (hasOverrideActualHours) {
            // Show both scheduled and actual hours when there's an override
            const actualHour = (data.overrideActualHours as { [key: string]: number | null })[day] || '0';
            return `SchdHour: ${scheduleHours}\nActualHour: ${actualHour}`;
          } else {
            // Show only scheduled hours when there's regular actual hour value
            return `SchdHour: ${scheduleHours}`;
          }
        };
        
        // Only for Saturday set horizontalOffset={92}, for other days use default
        return (
          <React.Fragment key={day}>
            <View style={[
              styles.inputWrapper,
              data.isSelected && styles.inputWrapperSelected,
              isHighlighted(day) && styles.inputWrapperHighlighted,
              (isDayDisabled(day) || !inputsEnabled) && styles.inputWrapperDisabled
            ]}>
              {day === 'saturday' ? (
                <DLOTooltip
                  content={getTooltipContent()}
                  position="right"
                  trigger="hover"
                  disabled={scheduleHours === undefined}
                  horizontalOffset={75}
                >
                  <TextInput
                    style={[
                      styles.hourInput,
                      { width: 74 }, // 92 - 16 = 74 for weekdays (adjusted for 15% scaling)
                      data.isSelected && styles.hourInputSelected,
                      (isDayDisabled(day) || !inputsEnabled) && styles.hourInputDisabled,
                      isHighlighted(day) && styles.hourInputHighlighted,
                      hasValidationError && styles.hourInputValidationError
                    ]}
                    value={data[day as keyof ActualHoursEmployeeRowData] as string}
                    onChangeText={(isDayDisabled(day) || !inputsEnabled) ? undefined : (value) => handleHourChange(day, value)}
                    placeholder=""
                    keyboardType="numeric"
                    editable={!isDayDisabled(day) && inputsEnabled}
                    maxLength={10}
                    textAlign="center"
                    {...(Platform.OS === 'web' && {
                      inputMode: 'decimal' as any,
                      pattern: '[0-9]*',
                      title: hasValidationError ? validationMessage : undefined
                    })}
                  />
                </DLOTooltip>
              ) : (
                <DLOTooltip
                  content={getTooltipContent()}
                  position="right"
                  trigger="hover"
                  disabled={scheduleHours === undefined}
                >
                  <TextInput
                    style={[
                      styles.hourInput,
                      { width: 74 }, // 92 - 16 = 74 for weekdays (adjusted for 15% scaling)
                      data.isSelected && styles.hourInputSelected,
                      (isDayDisabled(day) || !inputsEnabled) && styles.hourInputDisabled,
                      isHighlighted(day) && styles.hourInputHighlighted,
                      hasValidationError && styles.hourInputValidationError
                    ]}
                    value={data[day as keyof ActualHoursEmployeeRowData] as string}
                    onChangeText={(isDayDisabled(day) || !inputsEnabled) ? undefined : (value) => handleHourChange(day, value)}
                    placeholder=""
                    keyboardType="numeric"
                    editable={!isDayDisabled(day) && inputsEnabled}
                    maxLength={10}
                    textAlign="center"
                    {...(Platform.OS === 'web' && {
                      inputMode: 'decimal' as any,
                      pattern: '[0-9]*',
                      title: hasValidationError ? validationMessage : undefined
                    })}
                  />
                </DLOTooltip>
              )}
              {hasValidationError && validationMessage && Platform.OS !== 'web' && (
                <View style={styles.validationIcon}>
                  <Text style={styles.validationIconText}>!</Text>
                </View>
              )}
            </View>
            <View style={styles.divider} />
          </React.Fragment>
        );
      })}

      {/* Total Actual Hours Field */}
      <View style={[
        styles.totalWrapper,
        data.isSelected && styles.inputWrapperSelected,
        isHighlighted('total') && styles.inputWrapperHighlighted,
        shouldHighlightCell('total') && styles.inputWrapperValidationError
      ]}>
        <TextInput
          style={[
            styles.totalInput,
            { width: 74 }, // 122 - 16 = 106 for total (adjusted for 15% scaling)
            data.isSelected && styles.hourInputSelected,
            isHighlighted('total') && styles.hourInputHighlighted,
            shouldHighlightCell('total') && styles.hourInputValidationError
          ]}
          value={calculateTotalHours()}
          placeholder=""
          keyboardType="numeric"
          editable={false}
          textAlign="center"
          {...(Platform.OS === 'web' && {
            title: shouldHighlightCell('total') ? getValidationMessage('total') : undefined
          })}
        />
        {shouldHighlightCell('total') && Platform.OS !== 'web' && (
          <View style={styles.validationIcon}>
            <Text style={styles.validationIconText}>!</Text>
          </View>
        )}
      </View>

      {/* Divider between Total and Schedule Variance */}
      <View style={styles.divider} />

      {/* Schedule Variance Field */}
      <View style={[
        styles.scheduleVarianceWrapper,
        data.isSelected && styles.inputWrapperSelected,
        isHighlighted('scheduleVariance') && styles.inputWrapperHighlighted
      ]}>
        <TextInput
          style={[
            styles.scheduleVarianceInput,
            { width: 74 }, // Same width as total for consistency
            data.isSelected && styles.hourInputSelected,
            isHighlighted('scheduleVariance') && styles.hourInputHighlighted
          ]}
          value={calculateScheduleVariance()}
          placeholder=""
          keyboardType="numeric"
          editable={false}
          textAlign="center"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    minHeight: 40,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: '#DFDFDF',
    alignItems: 'stretch',
  },
  containerSelected: {
    backgroundColor: '#E0F1FF',
  },
  containerHighlighted: {
    backgroundColor: '#EFF5FC',
  },
  checkboxContainer: {
    width: 43,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
  },
  checkboxContainerSelected: {
    backgroundColor: '#E0F1FF',
  },
  checkboxContainerHighlighted: {
    backgroundColor: '#EFF5FC',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#3A3A3A',
    borderRadius: 2,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxCheck: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  divider: {
    width: 1,
    height: 40,
    backgroundColor: '#DFDFDF',
  },
  dropdownSection: {
    width: 183,
    minHeight: 40,
    paddingHorizontal: 10,
    paddingVertical: 5,
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  dropdownSectionSelected: {
    backgroundColor: '#E0F1FF',
  },
  dropdownSectionHighlighted: {
    backgroundColor: '#EFF5FC',
  },
  roleSection: {
    width: 165,
    minHeight: 40,
    paddingHorizontal: 10,
    paddingVertical: 5,
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  shiftSection: {
    width: 165,
    minHeight: 40,
    paddingHorizontal: 10,
    paddingVertical: 5,
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  inputWrapper: {
    width: 92,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  inputWrapperSelected: {
    backgroundColor: '#E0F1FF',
  },
  inputWrapperHighlighted: {
    width: 92,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
    backgroundColor: '#EFF5FC', // Keep exact same as Schedule Hours
  },
  inputWrapperDisabled: {
    backgroundColor: '#F5F5F5',
  },
  totalWrapper: {
    width: 100,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    //paddingHorizontal: 10,
  },
  scheduleVarianceWrapper: {
    width: 100,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    //paddingHorizontal: 10,
  },
  deleteSection: {
    width: 65,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  deleteSectionSelected: {
    backgroundColor: '#E0F1FF',
  },
  hourInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    textAlign: 'center',
  },
  hourInputSelected: {
    backgroundColor: '#E0F1FF',
    borderColor: '#C0C0C0', // Keep standard border color
  },
  hourInputDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#999999',
    opacity: 0.8,
  },
  hourInputHighlighted: {
    backgroundColor: '#EFF5FC',
    borderColor: '#C0C0C0', // Keep standard border color
  },
  dropdownStyle: {
    borderColor: '#C0C0C0',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: '#FBFCFE',
    minHeight: 28,
    height: 28,
  },
  dropdownStyleSelected: {
    backgroundColor: '#E0F1FF',
    borderColor: '#C0C0C0', // Keep standard border color
  },
  dropdownStyleHighlighted: {
    backgroundColor: '#EFF5FC',
    borderColor: '#C0C0C0', // Keep standard border color
  },
  dropdownStyleDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    opacity: 0.7,
  },
  dropdownContainer: {
    borderColor: '#C0C0C0',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: '#FBFCFE',
    maxHeight: 200,
  },
  dropdownText: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownPlaceholder: {
    color: '#6C757D',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownListItemLabel: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownListItemContainer: {
    height: 30,
  },
  dropdownSearchContainer: {
    borderBottomWidth: 1,
    borderBottomColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    paddingHorizontal: 6,
    paddingVertical: 4,
    alignItems: 'stretch',
  },
    dropdownStyleError: {
    borderColor: '#FF4444',
    borderWidth: 2,
  },
  dropdownSearchInput: {
    color: '#3A3A3A',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    backgroundColor: '#FBFCFE',
    height: 24,
    flex: 1,
    minWidth: 0,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  loadingText: {
    marginLeft: 8,
    color: '#6C757D',
    fontSize: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  // Hours validation styles
  hourInputValidationError: {
    borderColor: '#FFD700', // Yellow border for validation errors
    borderWidth: 2,
    backgroundColor: '#FFFACD', // Light yellow background
  },
  inputWrapperValidationError: {
    backgroundColor: '#FFFACD', // Light yellow background for wrapper
  },
  validationIcon: {
    position: 'absolute',
    right: 2,
    top: 2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FFD700',
    justifyContent: 'center',
    alignItems: 'center',
  },
  validationIconText: {
    color: '#B8860B',
    fontSize: 10,
    fontWeight: 'bold',
  },
  // Specific styles for Total and Schedule Variance to ensure consistent text color
  totalInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A', // Always rgb(58, 58, 58)
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    textAlign: 'center',
  },
  scheduleVarianceInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A', // Always rgb(58, 58, 58)
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    textAlign: 'center',
  },
});

export default DLOCreateActualHoursEmployeeRow;